import { ChangeEvent, KeyboardEvent, useEffect, useId, useState } from 'react';
import clsx from 'clsx';
import styles from './EmojiPicker.module.scss';
import { InputShared } from './InputShared';
import { TextInput } from './TextInput';
import { FormFields } from '../form';

const EMOJI_PATTERN = /^\p{Emoji_Presentation}$/u;

// Navigation/editing keys that must keep working even though direct
// character entry is blocked (deleting, tabbing away, arrow-key nav).
const ALLOWED_KEYS = [
  'Backspace',
  'Delete',
  'Tab',
  'Escape',
  'Enter',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
];

const DEFAULT_OPTIONS = [
  '😀',
  '😎',
  '🤓',
  '🥳',
  '🦊',
  '🐱',
  '🐶',
  '🦄',
  '🌈',
  '⭐',
  '🔥',
  '🚀',
  '🍀',
  '🌮',
  '☕',
  '🎧',
];

interface Props {
  customLabel?: string;
  disabled?: boolean;
  label: string;
  name?: string;
  onValue?: (value: string) => void;
  options?: string[];
  value: string | null;
}

export function EmojiPicker({
  customLabel = 'Emoji personalizado',
  disabled = false,
  label,
  name,
  onValue,
  options = DEFAULT_OPTIONS,
  value,
}: Props) {
  const groupName = useId();
  const [hiddenValue, setHiddenValue] = useState(value ?? '');
  const [customFieldKey, setCustomFieldKey] = useState(0);

  useEffect(() => {
    setHiddenValue(value ?? '');
  }, [value]);

  function onClickOption(option: string) {
    setHiddenValue(option);
    onValue?.(option);
    // Force the custom-emoji field to remount empty — a preset pick
    // shouldn't leave a stale/mismatched value sitting in it.
    setCustomFieldKey((key) => key + 1);
  }

  function onChangeCustom(
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    const { value: nextValue } = event.currentTarget;
    if (!EMOJI_PATTERN.test(nextValue)) {
      // Reject pasted text too — typing is blocked at the keydown level,
      // but paste bypasses it and lands here instead.
      event.currentTarget.value = '';
      return;
    }
    setHiddenValue(nextValue);
    onValue?.(nextValue);
  }

  function onKeyDownCustom(
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    if (event.metaKey || event.ctrlKey || ALLOWED_KEYS.includes(event.key)) {
      return;
    }
    // Typing is blocked outright — the field only accepts an emoji pasted
    // in or inserted via an OS/browser emoji picker.
    event.preventDefault();
  }

  return (
    <FormFields direction="column">
      {name && <input name={name} type="hidden" value={hiddenValue} />}
      <InputShared label={label}>
        <div
          aria-label={label}
          className={clsx(styles['emoji-picker-grid'])}
          role="radiogroup"
        >
          {options.map((option) => (
            <label key={option} className={clsx(styles['emoji-picker-option'])}>
              <input
                checked={value === option}
                disabled={disabled}
                name={groupName}
                onChange={() => onClickOption(option)}
                type="radio"
                value={option}
              />
              {option}
            </label>
          ))}
        </div>
      </InputShared>
      <TextInput
        key={customFieldKey}
        defaultValue={
          customFieldKey === 0 && value && !options.includes(value)
            ? value
            : undefined
        }
        disabled={disabled}
        label={customLabel}
        maxLength={2}
        onChange={onChangeCustom}
        onKeyDown={onKeyDownCustom}
        type="text"
      />
    </FormFields>
  );
}

export default EmojiPicker;
